// DrugReviewTab.tsx — Admin approval queue for the Drug Master normalization
// backfill (scripts/normalize-drug-master.js). Every proposal here is a
// script-suggested grouping of existing `medicines` rows that MIGHT be the
// same clinical drug; nothing is written to drug_master until an admin
// approves (optionally after editing the proposed name or removing a
// wrongly-grouped medicine). See
// docs/HMS_v4_Drug_Master_Formulary_Architecture.md §11-§12, §20.
//
// Deliberately does not expose confidence_tier/match_confidence anywhere
// outside this admin view (the architecture doc's UX Requirements say a
// prescribing doctor must never see them) — this tab is the one place
// they're shown, since here they're exactly the point.
import { useState, useEffect } from 'react';
import { getDrugReviewQueue, approveDrugReview, rejectDrugReview } from '../api';
import type { DrugReviewProposal } from '../types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Checkbox';

const TIER_META: Record<string, { label: string; bg: string; border: string; text: string }> = {
  high: { label: 'High confidence', bg: '#D1FAE5', border: '#A7F3D0', text: '#065F46' },
  medium: { label: 'Medium confidence', bg: '#FEF3C7', border: '#FDE68A', text: '#92400E' },
  low: { label: 'Low confidence', bg: '#FEE2E2', border: '#FECACA', text: '#991B1B' },
};

function ProposalCard({ proposal, onDone }: { proposal: DrugReviewProposal; onDone: () => void }) {
  const [canonicalName, setCanonicalName] = useState(proposal.proposed_canonical_name);
  const [genericName, setGenericName] = useState(proposal.proposed_generic_name);
  const [included, setIncluded] = useState<Set<number>>(new Set(proposal.medicine_ids));
  const [rejecting, setRejecting] = useState(false);
  const [rejectNotes, setRejectNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const tier = TIER_META[proposal.confidence_tier]!;

  const approve = async () => {
    setBusy(true); setError('');
    try {
      await approveDrugReview(proposal.id, {
        medicine_ids: Array.from(included),
        canonical_name: canonicalName.trim(),
        generic_name: genericName.trim(),
      });
      onDone();
    } catch (e: any) { setError(e.message); }
    setBusy(false);
  };

  const reject = async () => {
    setBusy(true); setError('');
    try {
      await rejectDrugReview(proposal.id, rejectNotes.trim());
      onDone();
    } catch (e: any) { setError(e.message); }
    setBusy(false);
  };

  const toggle = (id: number) => {
    setIncluded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <Card className="p-3.5">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1, minWidth: 260 }}>
          <Input value={canonicalName} onChange={(e) => setCanonicalName(e.target.value)} className="text-[13px] font-semibold" style={{ maxWidth: 320 }} />
          <span style={{ fontSize: 11, color: '#9CA3AF' }}>generic:</span>
          <Input value={genericName} onChange={(e) => setGenericName(e.target.value)} className="text-[12px]" style={{ maxWidth: 180 }} />
        </div>
        <span className="badge" style={{ background: tier.bg, color: tier.text, border: `1px solid ${tier.border}` }}>{tier.label}</span>
      </div>

      {proposal.match_basis && (
        <div style={{ fontSize: 11.5, color: '#6B7280', marginBottom: 10, fontStyle: 'italic' }}>{proposal.match_basis}</div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: '#6B7280', textAlign: 'left' }}>
              <th style={{ padding: '4px 6px', width: 28 }}></th>
              <th style={{ padding: '4px 6px' }}>Generic</th>
              <th style={{ padding: '4px 6px' }}>Brand</th>
              <th style={{ padding: '4px 6px' }}>Strength</th>
              <th style={{ padding: '4px 6px' }}>Form</th>
              <th style={{ padding: '4px 6px' }}>Schedule</th>
              <th style={{ padding: '4px 6px' }}>Manufacturer</th>
            </tr>
          </thead>
          <tbody>
            {proposal.medicines.map((m) => (
              <tr key={m.id} style={{ borderTop: '1px solid #F3F4F6', opacity: included.has(m.id) ? 1 : 0.4 }}>
                <td style={{ padding: '4px 6px' }}>
                  <Checkbox checked={included.has(m.id)} onCheckedChange={() => toggle(m.id)} disabled={proposal.status !== 'pending'} />
                </td>
                <td style={{ padding: '4px 6px' }}>{m.generic_name}</td>
                <td style={{ padding: '4px 6px' }}>{m.brand_name || '—'}</td>
                <td style={{ padding: '4px 6px' }}>{m.strength || '—'}</td>
                <td style={{ padding: '4px 6px' }}>{m.dosage_form || '—'}</td>
                <td style={{ padding: '4px 6px' }}>{m.schedule}</td>
                <td style={{ padding: '4px 6px' }}>{m.manufacturer || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {error && <div style={{ marginTop: 10, padding: '6px 10px', background: '#FEE2E2', color: '#991B1B', borderRadius: 'var(--radius)', fontSize: 12 }}>{error}</div>}

      {proposal.status === 'pending' && !rejecting && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <Button variant="success" size="sm" disabled={busy || included.size === 0} onClick={approve}>
            {busy ? 'Approving…' : included.size === proposal.medicine_ids.length ? '✓ Approve' : '✓ Approve (edited group)'}
          </Button>
          <Button variant="danger" size="sm" disabled={busy} onClick={() => setRejecting(true)}>✕ Reject</Button>
        </div>
      )}
      {rejecting && (
        <div style={{ marginTop: 10 }}>
          <Textarea placeholder="Why doesn't this belong together? (optional)" value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)} className="mb-1.5 h-[52px] text-xs" />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="danger" size="sm" disabled={busy} onClick={reject}>Confirm reject</Button>
            <Button variant="ghost" size="sm" onClick={() => setRejecting(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function DrugReviewTab() {
  const [proposals, setProposals] = useState<DrugReviewProposal[] | null>(null);
  const [error, setError] = useState('');

  const load = () => getDrugReviewQueue('pending').then(setProposals).catch((e: any) => setError(e.message));
  useEffect(() => { load(); }, []);

  if (!proposals) return <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading review queue…</div>;

  return (
    <div>
      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 16, lineHeight: 1.6 }}>
        Proposed groupings of existing medicines into a single clinical drug (drug_master), generated by the
        normalization backfill script. Nothing here is applied until you approve it — edit the name, uncheck a
        medicine that doesn't actually belong in the group, or reject the whole proposal. Approving creates a new
        clinical drug and links every checked medicine to it; this cannot be split apart automatically afterward.
      </div>
      {error && <div style={{ marginBottom: 14, padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', borderRadius: 'var(--radius)', fontSize: 12 }}>{error}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {proposals.map((p) => <ProposalCard key={p.id} proposal={p} onDone={load} />)}
        {proposals.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: '#9CA3AF' }}>No pending drug-review proposals</div>}
      </div>
    </div>
  );
}
